<template>
  <bg-text
    type="h4"
    weight="regular"
  >
    Histórico de movimentações do bloqueio
  </bg-text>
  <bg-text
    type="label"
    weight="regular"
  >
    Veja todas as transações devolvidas ou movimentadas no período de até 3 meses relacionadas a este bloqueio.
  </bg-text>
  <div :class="$style.summary">
    <bg-stack space="x-sm">
      <bg-text
        type="body-sm"
        weight="regular"
      >
        Valor total a ser devolvido via MED
      </bg-text>
      <refund-status-badge :status="infractionData.refund.status" />
      <bg-text
        type="h6"
        weight="bold"
      >
        {{ centsToReal(infractionData.refund.refunded_amount) }} / {{ centsToReal(infractionData.amount) }}
      </bg-text>
    </bg-stack>
  </div>
  <tr-form
    name="searchForm"
    :on-valid-submit="onSubmitFilters"
    :class="$style.filter"
  >
    <tr-text-field
      v-model="searchTerm"
      v-tooltip="onSubmitFilters"
      name="search"
      placeholder="Pesquise pelo identificador da devolução"
      dont-use-col-classes="true"
      :classes="$style.filterInput"
      :input-search="true"
      :no-margin="true"
    />
  </tr-form>
  <div
    v-if="!loading && refunds.length === 0"
    :class="$style.emptyState"
  >
    <bg-text
      is="p"
      type="h6"
      weight="regular"
    >
      Não há devoluções a serem listadas
    </bg-text>
  </div>
  <template v-else>
    <tr-table
      :class="$style.table"
      :no-margin="true"
    >
      <tr-thead>
        <tr-tr>
          <tr-th>Status</tr-th>
          <tr-th>Data da devolução</tr-th>
          <tr-th>
            Valor devolvido
          </tr-th>
          <tr-th>Identificador da devolução</tr-th>
        </tr-tr>
      </tr-thead>
      <tr-tbody>
        <tr-tr
          v-for="refund in refunds"
          :key="refund.id"
        >
          <tr-td>
            <refund-attempt-status-badge :status="refund.attempt_status" />
          </tr-td>
          <tr-td>{{ formatDateTime(refund.attempt_date) || '---' }}</tr-td>
          <tr-td>{{ refund.amount ? centsToReal(refund.amount) : '---' }}</tr-td>
          <tr-td>{{ refund.return_id || '---' }}</tr-td>
        </tr-tr>
      </tr-tbody>
    </tr-table>
    <div :class="$style.pagination">
      <bg-pagination
        v-model:page-cursor="cursorPagination.pagination.pageCursor"
        :next="cursorPagination.pagination.next"
        :prev="cursorPagination.pagination.previous"
      />
    </div>
  </template>
</template>

<script lang="jsx" setup>
import { ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { BgText, BgBadge, BgPagination, BgStack } from '@transfeeradev/bridge';
import { usePagination } from '@/apps/payin/payins/pages/usePagination';
import { formatDateTime } from '@/commons/formatters/formatDate';
import { centsToReal } from '@/commons/formatters/currency';
import { useErrorHandler } from '@/commons/composables/useErrorHandler';
import infractionsResource from '@/apps/contestations/infractions/resources/infractions';

const refundAttemptStatusBadge = ({ status }) => {
  const map = {
    succeeded: {
      color: 'green-semi-light',
      label: 'Sucesso'
    },
    processing: {
      color: 'yellow-semi-light',
      label: 'Em processamento'
    },
    failed: {
      color: 'red-semi-light',
      label: 'Falhou'
    }
  };

  if (!status || !map[status]) {
    return '---';
  }

  return (
    <BgBadge size="sm" color={map[status].color}>
      {map[status].label}
    </BgBadge>
  );
};

const refundStatusBadge = ({ status }) => {
  let color = 'yellow-light';
  let label = 'Em monitoramento';

  if (status === 'closed') {
    color = 'green-semi-light';
    label = 'Finalizado';
  }

  return (
    <BgBadge size="sm" color={color}>
      {label}
    </BgBadge>
  );
};

const { handleError } = useErrorHandler();

const props = defineProps({
  infractionData: {
    type: Object,
    required: true
  }
});
const loading = ref(false);
const searchTerm = ref();
const refunds = ref([]);

const { cursorPagination, resetPagination } = usePagination(() => {
  fetchRefunds();
});

const onSubmitFilters = () => {
  resetPagination();
  fetchRefunds();
};

const fetchRefunds = useDebounceFn(async () => {
  loading.value = true;
  try {
    const response = await infractionsResource.getRefunds(props.infractionData.id, {
      search: searchTerm.value,
      page_cursor: cursorPagination.pagination.pageCursor,
      page_size: 10
    });

    refunds.value = response.items;
    cursorPagination.pagination.next = response.meta.next;
    cursorPagination.pagination.previous = response.meta.previous;
  } catch (e) {
    handleError(e, 'Ocorreu um erro ao buscar os reembolsos');
  } finally {
    loading.value = false;
  }
});

fetchRefunds();
</script>

<style scoped module>
.summary {
  position: relative;
  display: flex;
  border: 1px solid var(--bg-grey-light);
  border-radius: var(--bg-border-radius-md);
  padding: var(--bg-spacing-sm);
  margin: var(--bg-spacing-md) 0 var(--bg-spacing-sm);
}

.filter {
  position: relative;
  display: flex;
  margin-bottom: var(--bg-spacing-sm);
}

.filterInput {
  flex: 1;
}

.emptyState {
  text-align: center;
}

.table {
  border: 1px solid var(--bg-grey-light);
  border-radius: var(--bg-border-radius-md);
  border-collapse: separate;
  margin-bottom: 0;
}

.pagination {
  display: flex;
  justify-content: center;
}
</style>
